import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import sampleVendors from "../data/sampleVendors";
import { categoryLabel } from "../data/categories";
import { useLang } from "../i18n/LanguageContext";
import { useFavorites } from "../context/FavoritesContext";
import { trackView, trackClick } from "../lib/analytics";
import usePageMeta from "../hooks/usePageMeta";
import { isPremium, planLimit } from "../hooks/usePlanSettings";
import { formatPrice } from "../lib/price";

export default function VendorProfile() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { isFavorite, toggle } = useFavorites();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      let found = null;
      try {
        const snap = await getDoc(doc(db, "vendors", id));
        if (snap.exists()) found = { id: snap.id, ...snap.data() };
      } catch (err) {
        console.warn("Vendor fetch failed:", err.message);
      }
      if (!found) found = sampleVendors.find((v) => v.id === id) || null;
      if (alive) {
        setVendor(found);
        setActive(0);
        setLoading(false);
        window.scrollTo({ top: 0 });
        if (found) trackView(found.id);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  usePageMeta(
    vendor ? `${vendor.businessName} — Amora Weddings` : t("meta.home"),
    (vendor && vendor.description) || t("meta.homeDesc")
  );

  if (loading) {
    return (
      <>
        <Navbar />
        <Center>…</Center>
      </>
    );
  }

  if (!vendor) {
    return (
      <>
        <Navbar />
        <Center>
          <p>{t("profile.notFound")}</p>
          <Link to="/">← {t("profile.back")}</Link>
        </Center>
        <Footer />
      </>
    );
  }

  const saved = isFavorite(vendor.id);
  const typeLabel = vendor.accountType === "company" ? t("card.company") : t("card.individual");
  const premium = isPremium(vendor);
  const lim = planLimit(vendor.tier);
  const photos = (vendor.photos && vendor.photos.length ? vendor.photos : []).slice(0, lim.photos);
  const aboutText =
    premium || !vendor.description
      ? vendor.description
      : vendor.description.length > lim.desc
      ? vendor.description.slice(0, lim.desc).trimEnd() + "…"
      : vendor.description;
  const extraCats = premium && Array.isArray(vendor.extraCategories) ? vendor.extraCategories : [];
  const grad = `linear-gradient(140deg, ${(vendor.gradient && vendor.gradient[0]) || "#3a5a47"}, ${
    (vendor.gradient && vendor.gradient[1]) || "#2f4a3c"
  })`;
  const mainBg = photos.length ? `url(${photos[active]}) center/cover` : grad;

  return (
    <>
      <Navbar />
      <Wrap>
        <Back to="/">← {t("profile.back")}</Back>

        <Top>
          <Gallery>
            <Main style={{ background: mainBg }} />
            {photos.length > 1 && (
              <Thumbs>
                {photos.map((p, i) => (
                  <Thumb
                    key={p}
                    $on={i === active}
                    style={{ backgroundImage: `url(${p})` }}
                    onClick={() => setActive(i)}
                  />
                ))}
              </Thumbs>
            )}
          </Gallery>

          <Side>
            <Eyebrow>
              {categoryLabel(vendor.category, lang)}
              {extraCats.map((c) => ` · ${categoryLabel(c, lang)}`).join("")}
            </Eyebrow>
            <h1>
              {vendor.businessName}
              {premium && <Premium>✓ {t("plan.premium")}</Premium>}
            </h1>
            <MetaRow>
              <span>{vendor.city}</span>
              <i />
              <span>{typeLabel}</span>
              {formatPrice(vendor, lang === "sq") && (
                <>
                  <i />
                  <span className="price">{formatPrice(vendor, lang === "sq")}</span>
                </>
              )}
            </MetaRow>

            <Contact>
              <a className="call" href={`tel:${vendor.phone}`} onClick={() => trackClick(vendor.id)}>{t("card.call")}</a>
              <a className="wa" href={`https://wa.me/${vendor.whatsapp}`} target="_blank" rel="noreferrer" onClick={() => trackClick(vendor.id)}>{t("card.whatsapp")}</a>
              {premium && vendor.instagram && (
                <a className="ig" href={`https://instagram.com/${vendor.instagram}`} target="_blank" rel="noreferrer" onClick={() => trackClick(vendor.id)}>Instagram</a>
              )}
              {premium && vendor.website && (
                <a className="ig" href={vendor.website} target="_blank" rel="noreferrer" onClick={() => trackClick(vendor.id)}>{t("profile.website")}</a>
              )}
            </Contact>

            <SaveBtn $saved={saved} onClick={() => toggle(vendor.id)}>
              {saved ? `♥ ${t("profile.saved")}` : `♡ ${t("profile.save")}`}
            </SaveBtn>
          </Side>
        </Top>

        {aboutText && (
          <About>
            <h2>{t("profile.about")}</h2>
            <p>{aboutText}</p>
          </About>
        )}

        {premium && vendor.videoUrl && (
          <About>
            <h2>{t("profile.video")}</h2>
            <a href={vendor.videoUrl} target="_blank" rel="noreferrer" style={{ color: "#2F4A3C", fontWeight: 500 }}>
              ▶ {t("profile.watchVideo")}
            </a>
          </About>
        )}
      </Wrap>
      <Footer />
    </>
  );
}

const Center = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.inkSoft};
  a { color: ${({ theme }) => theme.colors.green}; font-weight: 500; }
`;

const Premium = styled.span`
  display: inline-block;
  margin-left: 10px;
  vertical-align: middle;
  font-family: ${({ theme }) => theme.font.body};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.greenDeep};
  background: ${({ theme }) => theme.colors.gold};
  padding: 4px 11px;
  border-radius: 100px;
`;

const Wrap = styled.div`
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding: 24px clamp(20px, 5vw, 40px) 70px;
`;

const Back = styled(Link)`
  display: inline-block;
  margin-bottom: 20px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.inkSoft};
  &:hover { color: ${({ theme }) => theme.colors.ink}; }
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: clamp(24px, 4vw, 48px);
  align-items: start;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const Gallery = styled.div``;

const Main = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: ${({ theme }) => theme.radius};
  border: 1px solid ${({ theme }) => theme.colors.hair};
`;

const Thumbs = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const Thumb = styled.button`
  width: 74px;
  height: 74px;
  border-radius: 12px;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.green : "transparent")};
  opacity: ${({ $on }) => ($on ? 1 : 0.7)};
  transition: 0.18s;
  &:hover { opacity: 1; }
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  h1 {
    font-family: ${({ theme }) => theme.font.display};
    font-weight: 500;
    font-size: clamp(30px, 4.5vw, 44px);
    line-height: 1.08;
    letter-spacing: -0.01em;
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.ink};
  }
`;

const Eyebrow = styled.span`
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.green};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.inkSoft};
  i { width: 3px; height: 3px; border-radius: 50%; background: ${({ theme }) => theme.colors.gold}; }
  .price { color: ${({ theme }) => theme.colors.green}; font-weight: 600; }
`;

const Contact = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 26px;
  flex-wrap: wrap;
  a {
    flex: 1;
    min-width: 110px;
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    padding: 13px;
    border-radius: 12px;
    transition: 0.18s;
  }
  a.call { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; }
  a.call:hover { background: ${({ theme }) => theme.colors.greenDeep}; }
  a.wa { background: ${({ theme }) => theme.colors.creamDeep}; color: ${({ theme }) => theme.colors.ink}; }
  a.wa:hover { background: ${({ theme }) => theme.colors.roseSoft}; }
  a.ig { background: ${({ theme }) => theme.colors.creamDeep}; color: ${({ theme }) => theme.colors.ink}; }
  a.ig:hover { background: ${({ theme }) => theme.colors.roseSoft}; }
`;

const SaveBtn = styled.button`
  margin-top: 12px;
  background: transparent;
  border: 1px solid ${({ $saved, theme }) => ($saved ? theme.colors.rose : theme.colors.hair)};
  color: ${({ $saved, theme }) => ($saved ? theme.colors.rose : theme.colors.inkSoft)};
  border-radius: 12px;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.18s;
  &:hover { border-color: ${({ theme }) => theme.colors.rose}; color: ${({ theme }) => theme.colors.rose}; }
`;

const About = styled.section`
  margin-top: clamp(36px, 6vw, 60px);
  max-width: 720px;
  h2 {
    font-family: ${({ theme }) => theme.font.display};
    font-weight: 500;
    font-size: 26px;
    margin-bottom: 12px;
  }
  p {
    color: ${({ theme }) => theme.colors.inkSoft};
    font-size: 16px;
    line-height: 1.7;
    white-space: pre-wrap;
  }
`;
